import { ArrowDownToLine, ArrowLeft, ArrowRightLeft, ArrowUpFromLine, Edit, Loader2, Pencil, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { Skeleton } from '@/components/ui/skeleton';
import {
  StockMovementModal,
  type StockMovementKind,
  type StockMovementSubmitPayload,
} from '@/components/ui/stock-movement-modal';
import { usePart } from '@/features/parts/queries';
import {
  useDeleteStockLot,
  useStockLot,
} from '@/features/stock-lots/queries';
import {
  isLowStock,
  isOverstock,
  resolveMaxStock,
  resolveMinStock,
  type StockLot,
} from '@/features/stock-lots/types';
import {
  useAdjustmentMovement,
  useConsumptionMovement,
  useReceiptMovement,
  useStockMovements,
  useTransferMovement,
} from '@/features/stock-movements/queries';
import {
  visualKindOf,
  type StockMovement,
} from '@/features/stock-movements/types';
import { useVessel } from '@/features/vessels/queries';
import { vesselDisplayName } from '@/features/vessels/types';
import { ApiError } from '@/lib/api';
import { cn } from '@/lib/utils';

function Field({
  label,
  value,
  fullWidth = false,
}: {
  label: string;
  value: React.ReactNode;
  fullWidth?: boolean;
}) {
  const display =
    value === null || value === undefined || value === '' ? '—' : value;
  return (
    <div className={cn(fullWidth && 'sm:col-span-2')}>
      <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </dt>
      <dd className="mt-1 text-sm">{display}</dd>
    </div>
  );
}

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{title}</CardTitle>
      </CardHeader>
      <CardContent>
        <dl className="grid gap-4 sm:grid-cols-2">{children}</dl>
      </CardContent>
    </Card>
  );
}

/**
 * MovementsHistoryCard — full-width timeline of every stock_movement
 * for this lot, ordered most-recent first. Plus four action buttons
 * (+Receipt / +Consume / +Adjust / +Transfer) in the card header
 * that open the StockMovementModal pre-configured for the relevant
 * movement kind.
 *
 * Each timeline row is colour-coded by visualKindOf(m):
 *   incoming  (receipt + transfer_in)        emerald — positive delta
 *   outgoing  (consumption + transfer_out)   red — negative delta
 *   adjustment (signed)                      amber — correction
 *
 * The four mutation hooks are wired here so the card stays a
 * self-contained "stock movements widget" that the detail page just
 * mounts — the parent doesn't need to know about kinds, payloads,
 * or invalidation rules.
 */
function MovementsHistoryCard({ lot }: { lot: StockLot }) {
  const { t } = useTranslation();
  const { data: movements, isLoading } = useStockMovements(lot.id);
  const { data: part } = usePart(lot.partId);
  const effectiveUnit = part?.unit ?? null;

  const receiptMut = useReceiptMovement(lot.id);
  const consumptionMut = useConsumptionMovement(lot.id);
  const adjustmentMut = useAdjustmentMovement(lot.id);
  const transferMut = useTransferMovement();

  const [openKind, setOpenKind] = useState<StockMovementKind | null>(null);

  // Active mutation depends on which kind is open. The pending flag
  // and error message both flow into the modal.
  const activeMut =
    openKind === 'receipt'
      ? receiptMut
      : openKind === 'consumption'
        ? consumptionMut
        : openKind === 'adjustment'
          ? adjustmentMut
          : openKind === 'transfer'
            ? transferMut
            : null;

  const errorMessage = (() => {
    if (!activeMut?.error) return null;
    if (activeMut.error instanceof ApiError) {
      return t(activeMut.error.key, {
        defaultValue: t('stockMovement.modal.saveError'),
        ...(activeMut.error.params ?? {}),
      });
    }
    return t('stockMovement.modal.saveError');
  })();

  const handleConfirm = (payload: StockMovementSubmitPayload) => {
    const onSuccess = () => {
      setOpenKind(null);
      receiptMut.reset();
      consumptionMut.reset();
      adjustmentMut.reset();
      transferMut.reset();
    };
    switch (payload.kind) {
      case 'receipt':
        receiptMut.mutate(
          { quantity: payload.quantity, reason: payload.reason },
          { onSuccess },
        );
        break;
      case 'consumption':
        consumptionMut.mutate(
          {
            quantity: payload.quantity,
            reason: payload.reason,
            relatedWorkOrderId: payload.relatedWorkOrderId,
          },
          { onSuccess },
        );
        break;
      case 'adjustment':
        adjustmentMut.mutate(
          { delta: payload.delta, reason: payload.reason },
          { onSuccess },
        );
        break;
      case 'transfer':
        transferMut.mutate(
          {
            sourceLotId: lot.id,
            destLotId: payload.destLotId,
            quantity: payload.quantity,
            reason: payload.reason,
          },
          { onSuccess },
        );
        break;
    }
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      // Close requested — only honour it if no mutation is pending.
      if (activeMut?.isPending) return;
      setOpenKind(null);
      receiptMut.reset();
      consumptionMut.reset();
      adjustmentMut.reset();
      transferMut.reset();
    }
  };

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-3 space-y-0">
          <CardTitle className="text-base">
            {t('stockMovement.history.title')}
          </CardTitle>
          <div className="flex flex-wrap gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => setOpenKind('receipt')}
              className="text-emerald-700"
            >
              <ArrowDownToLine aria-hidden />
              {t('stockMovement.modal.receipt.button')}
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setOpenKind('consumption')}
              className="text-red-700"
            >
              <ArrowUpFromLine aria-hidden />
              {t('stockMovement.modal.consumption.button')}
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setOpenKind('adjustment')}
              className="text-amber-700"
            >
              <Pencil aria-hidden />
              {t('stockMovement.modal.adjustment.button')}
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setOpenKind('transfer')}
            >
              <ArrowRightLeft aria-hidden />
              {t('stockMovement.modal.transfer.button')}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-2">
              {Array.from({ length: 3 }).map((_, i) => (
                <Skeleton key={i} className="h-14 w-full" />
              ))}
            </div>
          ) : (movements ?? []).length === 0 ? (
            <div className="rounded-md border border-dashed p-4 text-center text-sm text-muted-foreground">
              {t('stockMovement.history.empty')}
            </div>
          ) : (
            <ul className="space-y-2">
              {(movements ?? []).map((m) => (
                <MovementRow key={m.id} movement={m} />
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      {openKind !== null && (
        <StockMovementModal
          open
          onOpenChange={handleOpenChange}
          kind={openKind}
          currentLot={lot}
          effectiveUnit={effectiveUnit}
          errorMessage={errorMessage}
          loading={Boolean(activeMut?.isPending)}
          onConfirm={handleConfirm}
        />
      )}
    </>
  );
}

function MovementRow({ movement }: { movement: StockMovement }) {
  const { t } = useTranslation();
  const kind = visualKindOf(movement);
  const tone =
    kind === 'incoming'
      ? 'border-l-emerald-500 bg-emerald-50/50'
      : kind === 'outgoing'
        ? 'border-l-red-500 bg-red-50/50'
        : 'border-l-amber-500 bg-amber-50/50';
  const deltaSign = movement.quantityDelta >= 0 ? '+' : '';
  const deltaTone =
    movement.quantityDelta > 0
      ? 'text-emerald-700'
      : movement.quantityDelta < 0
        ? 'text-red-700'
        : 'text-muted-foreground';

  return (
    <li className={cn('rounded-md border-l-4 border bg-card p-3 text-sm', tone)}>
      <div className="flex flex-wrap items-center gap-3">
        <span className="rounded-md bg-background px-2 py-0.5 text-xs font-medium uppercase">
          {t(`stockMovement.type.${movement.type}`)}
        </span>
        <span className={cn('font-mono font-semibold tabular-nums', deltaTone)}>
          {deltaSign}
          {movement.quantityDelta}
        </span>
        <span className="text-xs text-muted-foreground">
          {t('stockMovement.history.afterLabel')}{' '}
          <span className="font-mono tabular-nums text-foreground">
            {movement.quantityAfter}
          </span>
        </span>
        <span className="ml-auto text-xs text-muted-foreground">
          {new Date(movement.createdAt).toLocaleString()}
        </span>
      </div>
      {movement.reason && (
        <p className="mt-1 italic text-muted-foreground">{movement.reason}</p>
      )}
      {movement.relatedWorkOrderId && (
        <p className="mt-1 text-xs">
          <Link
            to={`/work-orders/${movement.relatedWorkOrderId}`}
            className="text-primary hover:underline"
          >
            {t('stockMovement.history.workOrderLink')}
          </Link>
        </p>
      )}
    </li>
  );
}

function DetailContent({ lot }: { lot: StockLot }) {
  const { t } = useTranslation();
  const { data: part } = usePart(lot.partId);
  const { data: vessel } = useVessel(lot.vesselId);

  const effMin = resolveMinStock(lot, part?.minStockGlobal);
  const effMax = resolveMaxStock(lot, part?.maxStockGlobal);
  const lowStock = isLowStock(lot, part?.minStockGlobal);
  const overStock = isOverstock(lot, part?.maxStockGlobal);
  const effectiveUnit = part?.unit ?? null;

  const stockTone = lowStock
    ? 'bg-red-100 text-red-700'
    : overStock
      ? 'bg-amber-100 text-amber-800'
      : 'bg-emerald-100 text-emerald-700';

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <Section title={t('stockLot.detail.sectionIdentity')}>
        <Field
          label={t('stockLot.fields.part')}
          value={
            part ? (
              <Link
                to={`/parts/${part.id}`}
                className="text-primary hover:underline"
              >
                <span className="font-mono">{part.code}</span>
                {' — '}
                {part.name}
              </Link>
            ) : (
              <span className="font-mono">{lot.partId}</span>
            )
          }
        />
        <Field
          label={t('stockLot.fields.vessel')}
          value={
            vessel ? (
              <Link
                to={`/vessels/${vessel.id}`}
                className="text-primary hover:underline"
              >
                {vesselDisplayName(vessel)}
              </Link>
            ) : (
              <span className="font-mono">{lot.vesselId}</span>
            )
          }
        />
        <Field
          label={t('stockLot.fields.location')}
          value={lot.location}
          fullWidth
        />
        <Field
          label={t('stockLot.fields.notes')}
          value={
            lot.notes ? <p className="whitespace-pre-line">{lot.notes}</p> : null
          }
          fullWidth
        />
      </Section>

      <Section title={t('stockLot.detail.sectionStock')}>
        <Field
          label={t('stockLot.fields.quantityOnHand')}
          value={
            <span
              className={cn(
                'inline-flex items-center rounded-md px-2 py-0.5 text-base font-semibold tabular-nums',
                stockTone,
              )}
            >
              {lot.quantityOnHand}
              {effectiveUnit && (
                <span className="ml-1 font-mono text-xs font-normal opacity-70">
                  {effectiveUnit}
                </span>
              )}
            </span>
          }
        />
        <Field
          label={t('stockLot.fields.lastMovementAt')}
          value={
            lot.lastMovementAt
              ? new Date(lot.lastMovementAt).toLocaleString()
              : null
          }
        />
        <Field
          label={t('stockLot.fields.minStockEffective')}
          value={
            <span className="tabular-nums">
              {effMin}
              <span className="ml-2 text-xs text-muted-foreground">
                ({t(
                  lot.minStockOverride !== null
                    ? 'stockLot.detail.override'
                    : 'stockLot.detail.inherited',
                )})
              </span>
            </span>
          }
        />
        <Field
          label={t('stockLot.fields.maxStockEffective')}
          value={
            Number.isFinite(effMax) ? (
              <span className="tabular-nums">
                {effMax}
                <span className="ml-2 text-xs text-muted-foreground">
                  ({t(
                    lot.maxStockOverride !== null
                      ? 'stockLot.detail.override'
                      : 'stockLot.detail.inherited',
                  )})
                </span>
              </span>
            ) : (
              t('stockLot.detail.noCap')
            )
          }
        />
      </Section>
    </div>
  );
}

export function StockLotDetailPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { data: lot, isLoading, isError, error, refetch } = useStockLot(id);
  const deleteMut = useDeleteStockLot();
  const [confirmOpen, setConfirmOpen] = useState(false);

  const errorMessage = (() => {
    if (!isError) return null;
    if (error instanceof ApiError) {
      return t(error.key, {
        defaultValue: t('stockLot.detail.loadError'),
        ...(error.params ?? {}),
      });
    }
    return t('stockLot.detail.loadError');
  })();

  const onConfirmDelete = () => {
    if (!id) return;
    deleteMut.mutate(id, {
      onSuccess: () => {
        setConfirmOpen(false);
        navigate('/stock-lots');
      },
    });
  };

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Button
            asChild
            variant="ghost"
            size="icon"
            aria-label={t('common.back')}
          >
            <Link to="/stock-lots">
              <ArrowLeft aria-hidden />
            </Link>
          </Button>
          <h1 className="text-2xl font-semibold">
            {lot ? lot.location : t('common.loading')}
          </h1>
        </div>
        {lot && (
          <div className="flex gap-2">
            <Button asChild variant="outline">
              <Link to={`/stock-lots/${lot.id}/edit`}>
                <Edit aria-hidden />
                {t('common.edit')}
              </Link>
            </Button>
            <Button
              variant="destructive"
              onClick={() => setConfirmOpen(true)}
              disabled={deleteMut.isPending}
            >
              {deleteMut.isPending ? (
                <Loader2 className="animate-spin" aria-hidden />
              ) : (
                <Trash2 aria-hidden />
              )}
              {t('common.delete')}
            </Button>
          </div>
        )}
      </header>

      {errorMessage && (
        <Alert variant="destructive">
          <AlertTitle>{t('stockLot.detail.loadError')}</AlertTitle>
          <AlertDescription className="space-y-2">
            <p>{errorMessage}</p>
            <Button variant="outline" size="sm" onClick={() => refetch()}>
              {t('common.retry')}
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {isLoading && (
        <div className="space-y-4">
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-32 w-full" />
        </div>
      )}

      {lot && (
        <>
          <DetailContent lot={lot} />
          <MovementsHistoryCard lot={lot} />
        </>
      )}

      {lot && (
        <ConfirmDialog
          open={confirmOpen}
          onOpenChange={setConfirmOpen}
          title={t('stockLot.delete.title')}
          description={t('stockLot.delete.description', {
            location: lot.location,
          })}
          confirmLabel={t('stockLot.delete.confirm')}
          variant="destructive"
          loading={deleteMut.isPending}
          onConfirm={onConfirmDelete}
        />
      )}
    </div>
  );
}
